/**
 * RecipeLineEditor Component
 *
 * Dialog for adding or editing a recipe line (ingredient).
 * Provides inventory item selection, quantity, unit, and per-unit fields.
 *
 * US-P2-010: Process Recipe Integration - Task 3
 */

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import Combobox from '@/components/ui/combobox'
import { useInventoryItems } from '@/hooks/useInventoryItems'
import { PER_UNIT_OPTIONS } from '@/hooks/useRecipeTemplates'
import { Package, AlertCircle, Save, X } from 'lucide-react'

/**
 * Unit options for recipe lines
 */
const UNIT_OPTIONS = [
  { value: 'kg', label: 'kg (kilograms)' },
  { value: 'g', label: 'g (grams)' },
  { value: 'L', label: 'L (litres)' },
  { value: 'ml', label: 'ml (millilitres)' },
  { value: 'each', label: 'each (units)' }
]

/**
 * RecipeLineEditor - Modal for editing recipe ingredients
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onOpenChange - Callback to control dialog open state
 * @param {Object} line - Existing line to edit (null for new)
 * @param {function} onSave - Callback with line data when saved
 * @param {boolean} saving - Whether a save is in progress
 */
export default function RecipeLineEditor({
  open,
  onOpenChange,
  line = null,
  onSave,
  saving = false
}) {
  const { allItems: inventoryItems, isLoading: loadingItems } = useInventoryItems({ activeOnly: true })

  // Form state
  const [formData, setFormData] = useState({
    inventory_item_id: '',
    quantity: '',
    unit: 'kg',
    per_unit: 'per_block',
    is_optional: false,
    notes: ''
  })
  const [errors, setErrors] = useState({})

  // Reset form when line changes or dialog opens
  useEffect(() => {
    if (open) {
      if (line) {
        setFormData({
          inventory_item_id: line.inventory_item_id || '',
          quantity: line.quantity?.toString() || '',
          unit: line.unit || 'kg',
          per_unit: line.per_unit || 'per_block',
          is_optional: line.is_optional || false,
          notes: line.notes || ''
        })
      } else {
        setFormData({
          inventory_item_id: '',
          quantity: '',
          unit: 'kg',
          per_unit: 'per_block',
          is_optional: false,
          notes: ''
        })
      }
      setErrors({})
    }
  }, [open, line])

  // Transform inventory items for combobox
  const inventoryOptions = inventoryItems.map(item => ({
    value: item.id,
    label: `${item.name} (${item.category})`
  }))

  // Get selected item info
  const selectedItem = inventoryItems.find(item => item.id === formData.inventory_item_id)

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when field is modified
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = () => {
    const newErrors = {}

    if (!formData.inventory_item_id) {
      newErrors.inventory_item_id = 'Please select an inventory item'
    }

    if (!formData.quantity || Number(formData.quantity) <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0'
    }

    if (!formData.unit) {
      newErrors.unit = 'Please select a unit'
    }

    if (!formData.per_unit) {
      newErrors.per_unit = 'Please select a per-unit type'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = () => {
    if (!validate()) return

    onSave({
      inventory_item_id: formData.inventory_item_id,
      quantity: Number(formData.quantity),
      unit: formData.unit,
      per_unit: formData.per_unit,
      is_optional: formData.is_optional,
      notes: formData.notes || null
    })
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {line ? 'Edit Ingredient' : 'Add Ingredient'}
          </DialogTitle>
          <DialogDescription>
            {line
              ? 'Modify the ingredient details below'
              : 'Add an ingredient to this recipe'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Inventory Item Selector */}
          <div className="space-y-2">
            <Label>
              Inventory Item <span className="text-destructive">*</span>
            </Label>
            {loadingItems ? (
              <div className="h-9 flex items-center text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin mr-2" />
                Loading inventory...
              </div>
            ) : (
              <Combobox
                value={formData.inventory_item_id}
                onChange={(value) => handleFieldChange('inventory_item_id', value)}
                options={inventoryOptions}
                placeholder="Select an inventory item"
                searchPlaceholder="Search inventory..."
                emptyMessage="No items found"
              />
            )}
            {errors.inventory_item_id && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.inventory_item_id}
              </p>
            )}
            {selectedItem && (
              <p className="text-sm text-muted-foreground">
                Category: {selectedItem.category} | Unit: {selectedItem.unit_of_measure}
              </p>
            )}
          </div>

          {/* Quantity and Unit Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">
                Quantity <span className="text-destructive">*</span>
              </Label>
              <Input
                id="quantity"
                type="number"
                step="0.001"
                min="0"
                value={formData.quantity}
                onChange={(e) => handleFieldChange('quantity', e.target.value)}
                placeholder="e.g., 0.5"
              />
              {errors.quantity && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.quantity}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit">
                Unit <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.unit}
                onValueChange={(value) => handleFieldChange('unit', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.unit && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.unit}
                </p>
              )}
            </div>
          </div>

          {/* Per Unit Selector */}
          <div className="space-y-2">
            <Label htmlFor="per_unit">
              Scale By <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.per_unit}
              onValueChange={(value) => handleFieldChange('per_unit', value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_UNIT_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {formData.per_unit === 'per_batch'
                ? 'Fixed quantity per batch regardless of size'
                : `Quantity multiplied by number of ${formData.per_unit.replace('per_', '')}s`
              }
            </p>
            {errors.per_unit && (
              <p className="text-sm text-destructive flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {errors.per_unit}
              </p>
            )}
          </div>

          {/* Optional Checkbox */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="is_optional"
              checked={formData.is_optional}
              onCheckedChange={(checked) => handleFieldChange('is_optional', checked)}
            />
            <Label htmlFor="is_optional" className="text-sm font-normal cursor-pointer">
              This ingredient is optional (can be substituted or omitted)
            </Label>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleFieldChange('notes', e.target.value)}
              placeholder="Optional notes about this ingredient..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {line ? 'Update' : 'Add'} Ingredient
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
